import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { ArrowLeft, Music, Gamepad2, Globe, Youtube, Volume2, Wind, Timer, Info, X, Send, Mic, MicOff, Sparkles, ZoomIn, ZoomOut, LayoutGrid } from "lucide-react";
import { ScrollArea } from "../components/ui/scroll-area";
import { Button } from "../components/ui/button";
import { getDeviceId } from "./deviceId";
import { API, LOGO_IMAGE, BACKGROUND_MUSIC, VOICE_OPTIONS, TEEN_EMOTIONS, TEEN_QUICK_MSGS, AAC_CARDS, TINY_AAC_CARDS } from "./constants";
import ChatMessage from "./ChatMessage";
import TypingIndicator from "./TypingIndicator";
import EmotionFace from "./EmotionFace";
import BreathingExercise from "./BreathingExercise";
import PuzzleGame from "./PuzzleGame";
import BubblePopGame from "./BubblePopGame";
import MemoryMatchGame from "./MemoryMatchGame";
import PoodleMaths from "./PoodleMaths";
import ColouringBook from "./ColouringBook";
import SpotDifference from "./SpotDifference";
import ParentsCorner from "./ParentsCorner";
import CalmDownTimer from "./CalmDownTimer";
import PictureCardsModal from "./PictureCardsModal";
import AvatarMaker from "./AvatarMaker";
import LanguageToggleModal from "./LanguageToggleModal";
import LearningTowers, { TOWERS } from "./LearningTowers";
import { getCardResponse, getEmotionResponse, getQuickMsgResponse } from "./cardResponses";
import PoodleTalkSignInPill from "./poodletalk/PoodleTalkSignInPill";
import { ptAuthHeaders } from "./poodletalk/ptAuth";
import { hotelAuthHeaders, getCachedHotel, isHotelModeActive } from "./poodletalk/hotelClient";

const MAX_FREE_MESSAGES = 10;

const ChatScreen = ({ character, onBack, isMusicPlaying, setIsMusicPlaying, audioRef, currentSong, setCurrentSong, selectedVoice, setSelectedVoice, autoRead, setAutoRead, bigText, setBigText, ageMode, setAgeMode, onNavigate }) => {
  // Hotel Mode: prepend "Welcome to <hotel welcome name>!" to the selected
  // poodle's normal greeting. This is a client-side template — no LLM call,
  // no credit deduction — and only activates when a validated hotel session
  // is present on this iPad. Family/guest sessions are untouched.
  const [hotelSnapshot, setHotelSnapshot] = useState(() => isHotelModeActive() ? getCachedHotel() : null);
  useEffect(() => {
    const on = () => setHotelSnapshot(isHotelModeActive() ? getCachedHotel() : null);
    window.addEventListener("pt_hotel_changed", on);
    return () => window.removeEventListener("pt_hotel_changed", on);
  }, []);
  const orgTotalCredits = hotelSnapshot ? ((hotelSnapshot.monthly_credits_remaining || 0) + (hotelSnapshot.addon_credits_remaining || 0)) : 0;
  const orgCounterLabel = hotelSnapshot ? ({
    hotel: "Hotel credits",
    school: "School credits",
    charity: "Organisation credits",
  }[hotelSnapshot.sector] || "Organisation credits") : "";
  const welcomeName = (hotelSnapshot && hotelSnapshot.welcome_name) ? String(hotelSnapshot.welcome_name).trim() : "";
  const initialGreeting = welcomeName
    ? `Welcome to ${welcomeName}! ${character.greeting}`
    : character.greeting;
  const [messages, setMessages] = useState([
    { role: 'assistant', content: initialGreeting }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [typingIndex, setTypingIndex] = useState(-1);
  const [showBreathing, setShowBreathing] = useState(false);
  const [showPuzzle, setShowPuzzle] = useState(false);
  const [showBubblePop, setShowBubblePop] = useState(false);
  const [showMemoryMatch, setShowMemoryMatch] = useState(false);
  const [showMaths, setShowMaths] = useState(false);
  const [showColouring, setShowColouring] = useState(false);
  const [showSpotDiff, setShowSpotDiff] = useState(false);
  const [showGamesMenu, setShowGamesMenu] = useState(false);
  const [showMusicMenu, setShowMusicMenu] = useState(false);
  const [showParentsCorner, setShowParentsCorner] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(null);
  const [showVoiceMenu, setShowVoiceMenu] = useState(false);
  const [showCalmTimer, setShowCalmTimer] = useState(false);
  const [showMoodCheck, setShowMoodCheck] = useState(false);
  const [showAAC, setShowAAC] = useState(false);
  const [showPictureCards, setShowPictureCards] = useState(false);
  const [showAvatarMaker, setShowAvatarMaker] = useState(false);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  // Saved bilingual selection — fetched once on mount, refreshed when the
  // family edits it. We don't read it in JSX (poodle uses it server-side),
  // but we send it on every /api/chat call so the system prompt stays in
  // sync if a user changes it mid-conversation on another device.
  const [secondaryLanguages, setSecondaryLanguages] = useState([]);
  const [showMoreFunctions, setShowMoreFunctions] = useState(false);
  // Learning Towers — kid taps a tower → Poodle becomes a tutor for that subject.
  // learningSubject is the tower key (e.g. "english"); null = free chat mode.
  const [showLearningTowers, setShowLearningTowers] = useState(false);
  const [learningSubject, setLearningSubject] = useState(null);
  // User's custom avatar from the Avatar Maker (persisted in localStorage so it
  // survives reloads). Shown next to user message bubbles in the chat.
  const [userAvatar, setUserAvatar] = useState(() => {
    try { return localStorage.getItem("poodletalk_user_avatar") || null; } catch { return null; }
  });
  const [userAvatarShape, setUserAvatarShape] = useState(() => {
    try { return localStorage.getItem("poodletalk_user_avatar_shape") || "frame-circle"; } catch { return "frame-circle"; }
  });

  // Listen for live updates when the kid saves a new avatar from the modal
  useEffect(() => {
    const onUpdate = (e) => {
      setUserAvatar(e?.detail?.dataUrl || null);
      if (e?.detail?.shape) setUserAvatarShape(e.detail.shape);
    };
    const onStorage = (e) => {
      if (e.key === "poodletalk_user_avatar") setUserAvatar(e.newValue || null);
      if (e.key === "poodletalk_user_avatar_shape") setUserAvatarShape(e.newValue || "frame-circle");
    };
    window.addEventListener("poodletalk_avatar_updated", onUpdate);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("poodletalk_avatar_updated", onUpdate);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  // First-time prompt: open Avatar Maker once if the kid has never seen it AND
  // hasn't already created an avatar. They can dismiss it any time.
  useEffect(() => {
    try {
      const seen = localStorage.getItem("poodletalk_avatar_prompt_seen");
      const has = localStorage.getItem("poodletalk_user_avatar");
      if (!seen && !has) {
        const t = setTimeout(() => {
          setShowAvatarMaker(true);
          localStorage.setItem("poodletalk_avatar_prompt_seen", "1");
        }, 1200); // small delay so the chat greeting renders first
        return () => clearTimeout(t);
      }
    } catch { /* localStorage blocked — ignore */ }
  }, []);
  const [isOnCooldown, setIsOnCooldown] = useState(false);
  const [cooldownTimeLeft, setCooldownTimeLeft] = useState('');
  const [globalCapHit, setGlobalCapHit] = useState(false);  // monthly free-user cap reached
  const [freeRemaining, setFreeRemaining] = useState(MAX_FREE_MESSAGES);
  const [paidRemaining, setPaidRemaining] = useState(0);
  const [redeemCode, setRedeemCode] = useState('');
  const [redeemMessage, setRedeemMessage] = useState('');
  const [redeemSuccess, setRedeemSuccess] = useState(false);
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [showLowCreditWarning, setShowLowCreditWarning] = useState(false);
  const messagesEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);

  // Fetch credit status from backend on mount and periodically.
  // Also re-fetches when the family signs in/out so credits update instantly.
  useEffect(() => {
    const deviceId = getDeviceId();
    const fetchCredits = async () => {
      // Hotel Mode: family credits/cooldown are irrelevant. Backend enforces
      // hotel-credit balance on every /api/chat call via the X-Hotel-Token
      // header, so skip this poll and never show the family cooldown screen.
      if (hotelAuthHeaders()["X-Hotel-Token"]) {
        setIsOnCooldown(false);
        return;
      }
      try {
        const res = await axios.get(`${API}/credits`, {
          params: { device_id: deviceId },
          headers: ptAuthHeaders(),
        });
        setFreeRemaining(res.data.free_remaining);
        setPaidRemaining(res.data.paid_remaining);
        if (res.data.cooldown) {
          setIsOnCooldown(true);
          const mins = res.data.cooldown_mins;
          setCooldownTimeLeft(mins > 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`);
        } else {
          setIsOnCooldown(false);
        }
      } catch (e) {
        console.error('Failed to fetch credits:', e);
      }
    };
    fetchCredits();
    const interval = setInterval(fetchCredits, 30000);
    const onAuth = () => fetchCredits();
    window.addEventListener("pt_auth_changed", onAuth);
    return () => {
      clearInterval(interval);
      window.removeEventListener("pt_auth_changed", onAuth);
    };
  }, []);

  // Fetch saved bilingual languages on mount + when family signs in/out.
  // We pass these on every /api/chat call so Claude mirrors the right language.
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const res = await axios.get(`${API}/languages`, {
          params: { device_id: getDeviceId() },
          headers: ptAuthHeaders(),
        });
        setSecondaryLanguages(res.data.selected || []);
      } catch {
        setSecondaryLanguages([]);
      }
    };
    fetchLanguages();
    const onAuth = () => fetchLanguages();
    window.addEventListener("pt_auth_changed", onAuth);
    return () => window.removeEventListener("pt_auth_changed", onAuth);
  }, []);

  // Close games menu on outside click
  useEffect(() => {
    if (!showGamesMenu) return;
    const handleClick = (e) => {
      if (!e.target.closest('[data-testid="games-menu"]') && !e.target.closest('[data-testid="games-button"]')) {
        setShowGamesMenu(false);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [showGamesMenu]);

  // Close "More functions" popover on outside click
  useEffect(() => {
    if (!showMoreFunctions) return;
    const handleClick = (e) => {
      if (!e.target.closest('[data-testid="more-functions-button"]') && !e.target.closest('[data-testid^="more-fn-"]')) {
        setShowMoreFunctions(false);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [showMoreFunctions]);

  const handleBack = useCallback(() => {
    if (messages.length > 2) {
      setShowMoodCheck(true);
    } else {
      onBack();
    }
  }, [messages.length, onBack]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  useEffect(() => {
    window.speechSynthesis?.getVoices();
    const handler = () => window.speechSynthesis?.getVoices();
    window.speechSynthesis?.addEventListener?.('voiceschanged', handler);
    return () => window.speechSynthesis?.removeEventListener?.('voiceschanged', handler);
  }, []);

  // ===== Learning Towers handlers =====
  const enterTower = useCallback((tower) => {
    // Gate the tower for free users: show a warm explainer up front and
    // stay in free chat mode so they aren't stuck in a "needs credits" loop.
    if (paidRemaining <= 0) {
      setShowLearningTowers(false);
      setMessages((prev) => {
        const explainer = {
          role: "assistant",
          content: `🏰 Ooh, the ${tower.label} tower! That's my special tutor mode 💛 A grown-up needs to add chat credits to unlock the towers. In the meantime we can keep chatting as normal — what would you like to talk about?`
        };
        const next = [...prev, explainer];
        setTypingIndex(next.length - 1);
        return next;
      });
      return;
    }
    setLearningSubject(tower.key);
    setShowLearningTowers(false);
    // Inject the welcome message as a poodle bubble so the kid sees it
    // immediately. The next API call will include this in the conversation
    // history so Claude knows the tower context too.
    setMessages((prev) => {
      const next = [...prev, { role: "assistant", content: tower.greeting }];
      setTypingIndex(next.length - 1);
      return next;
    });
  }, [paidRemaining]);

  const exitTower = useCallback(() => {
    const current = TOWERS.find((t) => t.key === learningSubject);
    setLearningSubject(null);
    if (current) {
      setMessages((prev) => {
        const farewell = ageMode === "teen"
          ? `Leaving the ${current.label} tower 👋 — back to free chat. Pick another tower anytime.`
          : `Bye ${current.label} tower! 👋 We can come back anytime. What shall we talk about now?`;
        const next = [...prev, { role: "assistant", content: farewell }];
        setTypingIndex(next.length - 1);
        return next;
      });
    }
  }, [learningSubject, ageMode]);

  const sendMessage = async (text) => {
    if (!text.trim() || isLoading || isOnCooldown) return;

    const userMessage = { role: 'user', content: text };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    // Per-send idempotency id. If axios retries or the user rapidly taps, the
    // backend uses this to guarantee we're charged at most once for this
    // particular message.
    const txId = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `tx_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

    try {
      const response = await axios.post(`${API}/chat`, {
        message: text,
        history: messages,
        character_name: character.name,
        age_mode: ageMode,
        learning_subject: learningSubject,
        local_time: new Date().toLocaleString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
        device_id: getDeviceId(),
        transaction_id: txId,
      }, { headers: { ...ptAuthHeaders(), ...hotelAuthHeaders() } });

      const data = response.data;

      // Update credit state from backend response
      if (data.free_remaining !== undefined) setFreeRemaining(data.free_remaining);
      if (data.paid_remaining !== undefined) {
        setPaidRemaining(data.paid_remaining);
        // Show low credit warning when paid credits drop to 10 or below
        if (data.credit_type === 'paid' && data.paid_remaining <= 10 && data.paid_remaining > 0) {
          setShowLowCreditWarning(true);
        }
      }

      // Refresh the cached hotel snapshot so the counter reflects any credit
      // used by this or ANOTHER device that just deducted from the shared balance.
      if (data.hotel_snapshot) {
        try {
          const { setCachedHotel } = await import("./poodletalk/hotelClient");
          setCachedHotel(data.hotel_snapshot);
          window.dispatchEvent(new CustomEvent("pt_hotel_changed"));
        } catch {}
      }

      // Learning Tower blocked for free users — auto-exit tower mode so they
      // can carry on chatting normally without crossing out manually.
      if (data.tower_paid_only) {
        setLearningSubject(null);
        const explainer = {
          role: 'assistant',
          content: "🏰 You found a Learning Tower! That's a special tutor mode that needs chat credits. I've stepped out of the tower so we can keep chatting normally 💛 Ask a grown-up if you'd like to unlock the tutor — they'll know what to do ✨"
        };
        setMessages(prev => [...prev, explainer]);
        return;
      }

      // If backend says cooldown (free + paid exhausted), DO NOT block the chat
      // UI any more (no overlay). Just append the sleepy-poodle message and keep
      // picture cards, emotion buttons and games fully usable. This avoids
      // z-index issues that were breaking the games button on the old overlay.
      if (data.cooldown) {
        setIsOnCooldown(true);
        setCooldownTimeLeft('');
        setGlobalCapHit(!!data.global_cap_hit);
        if (data.response) {
          const assistantMessage = { role: 'assistant', content: data.response };
          setMessages(prev => [...prev, assistantMessage]);
        }
        return;
      }

      const assistantMessage = { role: 'assistant', content: data.response };
      setMessages(prev => {
        const newMsgs = [...prev, assistantMessage];
        setTypingIndex(newMsgs.length - 1);
        if (autoRead) {
          const wordCount = data.response.split(' ').length;
          setTimeout(() => speakMessage(data.response, newMsgs.length - 1), wordCount * 60 + 300);
        }
        return newMsgs;
      });
    } catch (error) {
      console.error('Chat error:', error);
      const fallbackMessage = { role: 'assistant', content: "I'm here with you. Sometimes my ears get a bit fuzzy, can you say that again? 💛" };
      setMessages(prev => [...prev, fallbackMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  const handleEmotionClick = (emotion) => {
    const emotionMessages = {
      happy: "😊 I feel happy!",
      sad: "😢 I feel sad",
      nervous: "😰 I feel nervous",
      angry: "😠 I feel angry",
      calm: "😌 I feel calm",
      learning: "🤓 I feel like learning",
      stressed: "😣 I'm feeling stressed",
      anxious: "😟 I feel anxious",
      lonely: "🥺 I feel lonely",
      confused: "😕 I feel confused",
      okay: "🙂 I'm just okay"
    };
    sendCardMessage(emotionMessages[emotion] || `I feel ${emotion}`, () => getEmotionResponse(emotion));
    trackTap("emotion", emotion);
  };

  // Local card message — no API call, no credit cost
  // Fire-and-forget engagement tap tracker. Used for research only
  // ("how many ASD/SEND visitors communicate ONLY by tapping?").
  // Catches every error so the kid-facing UI is never blocked.
  //   tap_type: "card" | "emotion" | "quick_message" | "category_open" | "age_mode"
  //   detail:   optional small string (category key / emotion id / age mode)
  const trackTap = (tap_type, detail) => {
    try {
      axios.post(`${API}/track-tap`, { device_id: getDeviceId(), tap_type, detail }, { headers: ptAuthHeaders() })
        .catch(() => { /* silent: analytics must never disturb the experience */ });
    } catch { /* ignore */ }
  };

  // Record the current age mode (Tiny/Kids/Teen) once on mount and whenever
  // it changes. Lets us answer "do Tiny-mode kids tap more, do Teens type
  // more?" — research signal without any personal data.
  useEffect(() => {
    if (ageMode) trackTap("age_mode", ageMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ageMode]);

  const sendCardMessage = (text, getResponse) => {
    if (!text.trim() || isLoading) return;
    const userMessage = { role: 'user', content: text };
    const responseText = getResponse();
    const assistantMessage = { role: 'assistant', content: responseText };
    setMessages(prev => {
      const newMsgs = [...prev, userMessage, assistantMessage];
      setTypingIndex(newMsgs.length - 1);
      if (autoRead) {
        const wordCount = responseText.split(' ').length;
        setTimeout(() => speakMessage(responseText, newMsgs.length - 1), wordCount * 60 + 300);
      }
      return newMsgs;
    });
  };

  const handleRedeem = async (e) => {
    e.preventDefault();
    if (!redeemCode.trim() || isRedeeming) return;
    setIsRedeeming(true);
    setRedeemMessage('');
    try {
      const res = await axios.post(`${API}/redeem`, { code: redeemCode.trim(), device_id: getDeviceId() }, { headers: ptAuthHeaders() });
      if (res.data.success) {
        setRedeemSuccess(true);
        setRedeemMessage(res.data.message);
        setPaidRemaining(res.data.paid_remaining);
        setRedeemCode('');
        // Exit cooldown after successful redemption
        setTimeout(() => {
          setIsOnCooldown(false);
          setRedeemMessage('');
        }, 2000);
      } else {
        setRedeemSuccess(false);
        setRedeemMessage(res.data.message);
      }
    } catch (error) {
      setRedeemSuccess(false);
      setRedeemMessage('Something went wrong. Please try again.');
    } finally {
      setIsRedeeming(false);
    }
  };

  const toggleMusic = () => {
    if (audioRef.current) {
      if (isMusicPlaying) {
        audioRef.current.pause();
        setIsMusicPlaying(false);
      } else {
        audioRef.current.play();
        setIsMusicPlaying(true);
      }
    }
  };

  const selectSong = (index) => {
    setCurrentSong(index);
    if (audioRef.current) {
      audioRef.current.src = BACKGROUND_MUSIC[index].url;
      if (isMusicPlaying) {
        audioRef.current.play();
      }
    }
    setShowMusicMenu(false);
  };

  const findBestVoice = (voices, voiceId) => {
    const girlPreferences = ['Samantha', 'Karen', 'Tessa', 'Google UK English Female', 'Microsoft Zira', 'Moira', 'Fiona', 'Martha', 'Catherine'];
    const boyPreferences = ['Victoria'];
    const prefs = voiceId === 'shimmer' ? girlPreferences : boyPreferences;
    for (const name of prefs) {
      const match = voices.find(v => v.name.includes(name) && v.lang.startsWith('en'));
      if (match) return match;
    }
    const genderHint = voiceId === 'shimmer' ? 'female' : 'male';
    const englishVoice = voices.find(v => v.lang.startsWith('en') && v.name.toLowerCase().includes(genderHint));
    if (englishVoice) return englishVoice;
    return voices.find(v => v.lang.startsWith('en'));
  };

  // Bilingual TTS — split a multilingual reply into runs by writing system
  // (Latin / CJK / Arabic / Cyrillic / etc.) so each chunk gets read with a
  // voice that actually pronounces it. Falls back to English if no voice
  // exists for that script on the device.

  // For Latin chunks, try to detect French/Spanish/German/Italian/Polish by
  // common short signature words. Conservative: if unsure, return 'en'.
  const detectLatinLang = (chunk) => {
    const t = chunk.toLowerCase();
    if (/[ñ¿¡]|(\b(hola|gracias|cómo|qué|niño|niña|sí|tú|está|estás|mamá|papá|adiós)\b)/i.test(chunk)) return 'es';
    if (/[àâçéèêëîïôûùüœ]|(\b(bonjour|merci|comment|ça va|maman|papa|aujourd|oui|très|c'est)\b)/i.test(t)) return 'fr';
    if (/[äöüß]|(\b(hallo|danke|wie|geht|mama|papa|guten|ich|bist|nicht|sehr)\b)/i.test(t)) return 'de';
    if (/[àèéìòù]|(\b(ciao|grazie|come|stai|mamma|papà|molto|sì|bene)\b)/i.test(t)) return 'it';
    if (/[ąćęłńóśźż]|(\b(cześć|dzień|dobry|jak|mama|tata|tak|nie|proszę|dziękuję)\b)/i.test(t)) return 'pl';
    return 'en';
  };

  // Split text into [{text, lang}] runs. Keeps punctuation/spaces attached
  // to the previous run so the reader's prosody stays natural.
  const splitByLang = (text) => {
    const runs = [];
    let buf = '';
    let curLang = null;
    const flush = () => {
      if (buf.trim()) runs.push({ text: buf, lang: curLang });
      buf = '';
    };
    for (const ch of text) {
      const isCJK = /[\u4e00-\u9fff]/.test(ch);
      const isArabic = /[\u0600-\u06ff]/.test(ch);
      const isJapanese = /[\u3040-\u309f\u30a0-\u30ff]/.test(ch);
      const isKorean = /[\uac00-\ud7af]/.test(ch);
      let lang = 'latin';
      if (isCJK) lang = 'zh';
      else if (isArabic) lang = 'ar';
      else if (isJapanese) lang = 'ja';
      else if (isKorean) lang = 'ko';
      if (curLang === null) curLang = lang;
      if (lang !== curLang && /\S/.test(ch)) {
        flush();
        curLang = lang;
      }
      buf += ch;
    }
    flush();
    // Resolve Latin runs to specific languages
    return runs.map((r) => ({
      text: r.text,
      lang: r.lang === 'latin' ? detectLatinLang(r.text) : r.lang,
    }));
  };

  const pickVoiceForLang = (voices, lang) => {
    if (lang === 'en') return null; // caller uses findBestVoice
    // Per-language preferred FEMALE voice names so the multilingual reply
    // sounds cohesive (English voice is already female via findBestVoice).
    // The browser swaps the voice actor between language chunks, but at
    // least they'll all be female.
    const femalePrefs = {
      zh: ['Tingting', 'Yating', 'Mei-Jia', 'Sin-Ji', 'Huihui', 'Hanhan', 'Yaoyao', 'Xiaoxiao', 'Female'],
      fr: ['Amélie', 'Amelie', 'Audrey', 'Aurélie', 'Aurelie', 'Marie', 'Virginie', 'Hortense', 'Female'],
      es: ['Mónica', 'Monica', 'Paulina', 'Marisol', 'Helena', 'Sabina', 'Female'],
      de: ['Anna', 'Petra', 'Hedda', 'Katja', 'Female'],
      it: ['Alice', 'Federica', 'Paola', 'Isabella', 'Female'],
      pl: ['Zosia', 'Paulina', 'Female'],
      ar: ['Laila', 'Hoda', 'Maha', 'Salma', 'Female'],
      ja: ['Kyoko', 'O-Ren', 'Haruka', 'Ayumi', 'Female'],
      ko: ['Yuna', 'Sora', 'Heami', 'Female'],
      ru: ['Milena', 'Katya', 'Irina', 'Female'],
    };
    const langPrefixes = { zh: ['zh', 'cmn'], ar: ['ar'], fr: ['fr'], es: ['es'], de: ['de'], it: ['it'], pl: ['pl'], ja: ['ja'], ko: ['ko'], ru: ['ru'] }[lang] || [];
    // First pass: voices matching the language AND a known female name
    const wantedNames = femalePrefs[lang] || ['Female'];
    for (const prefix of langPrefixes) {
      for (const wantedName of wantedNames) {
        const v = voices.find(
          (vv) =>
            vv.lang.toLowerCase().startsWith(prefix) &&
            vv.name.toLowerCase().includes(wantedName.toLowerCase())
        );
        if (v) return v;
      }
    }
    // Second pass: any voice for the language (last resort — gender unknown)
    for (const prefix of langPrefixes) {
      const v = voices.find((vv) => vv.lang.toLowerCase().startsWith(prefix));
      if (v) return v;
    }
    return null;
  };

  const speakMessage = (text, messageIndex) => {
    if (!window.speechSynthesis) return;

    // Hard reset — cancel anything currently speaking AND clear any
    // pending utterances. On repeat-clicks Chrome's queue can hold stale
    // utterances that block new ones.
    try {
      window.speechSynthesis.cancel();
    } catch (_e) { /* ignore */ }

    // Slight delay so the cancel actually flushes before we queue again.
    setTimeout(() => {
      setIsSpeaking(messageIndex);
      const cleanText = text.replace(/[💛🌟✨🐩🎉😊😢😰😠😌🤓🙋🆘😴😕🥺😨🤩😟🥳🫣😒😤]/g, '');
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      // Re-fetch voices at speak time — on Chrome, the list can be empty
      // until the user interacts with the page; subsequent calls return more.
      let voices = window.speechSynthesis.getVoices();
      if (!voices || voices.length === 0) {
        // Trigger lazy load, then try once more
        window.speechSynthesis.getVoices();
        voices = window.speechSynthesis.getVoices();
      }
      const englishVoice = voices && voices.length > 0 ? findBestVoice(voices, selectedVoice) : null;

      const runs = splitByLang(cleanText);

      const baseSettings = selectedVoice === 'shimmer'
        ? { pitch: 1.35, rate: isSafari ? 0.95 : 1.2, volume: 0.65 }
        : { pitch: 1.0, rate: isSafari ? 0.85 : 1.0, volume: 0.7 };

      // Build all utterances first so we can chain them serially.
      const queue = runs.map((run) => {
        const u = new SpeechSynthesisUtterance(run.text);
        u.pitch = baseSettings.pitch;
        u.rate = baseSettings.rate;
        u.volume = baseSettings.volume;
        if (run.lang === 'en' && englishVoice) {
          u.voice = englishVoice;
          u.lang = englishVoice.lang || 'en-GB';
        } else if (run.lang !== 'en') {
          const v = pickVoiceForLang(voices || [], run.lang);
          if (v) {
            u.voice = v;
            u.lang = v.lang;
          } else {
            // No native voice installed — set the BCP-47 hint anyway so the
            // OS may swap in a system voice rather than mangling with English.
            u.lang = { zh: 'zh-CN', fr: 'fr-FR', es: 'es-ES', de: 'de-DE', it: 'it-IT', pl: 'pl-PL', ar: 'ar-SA', ja: 'ja-JP', ko: 'ko-KR', ru: 'ru-RU' }[run.lang] || 'en-GB';
          }
          u.pitch = Math.min(u.pitch, 1.15);
        }
        return u;
      });

      // Serial chain — when utterance N ends, start N+1. This avoids the
      // browser-queue race where Chrome silently drops mid-queue utterances
      // on a repeat click.
      let idx = 0;
      // Per-utterance watchdog — Chrome can simply not fire onend for some
      // non-English voices. If 6 seconds elapse with nothing started or
      // ended for the current utterance, advance to the next one.
      let watchdog = null;
      const armWatchdog = () => {
        if (watchdog) clearTimeout(watchdog);
        // 7s should comfortably fit any short reply chunk
        watchdog = setTimeout(() => {
          // Force-advance: cancel this utterance and start the next
          try { window.speechSynthesis.cancel(); } catch (_e) { /* ignore */ }
          idx += 1;
          playNext();
        }, 7000);
      };
      const clearWatchdog = () => { if (watchdog) { clearTimeout(watchdog); watchdog = null; } };

      const playNext = () => {
        if (idx >= queue.length) {
          clearWatchdog();
          setIsSpeaking(null);
          return;
        }
        const u = queue[idx];
        u.onend = () => {
          clearWatchdog();
          idx += 1;
          // Tiny delay before next utterance — gives Chrome time to reset
          setTimeout(playNext, 60);
        };
        u.onerror = () => {
          clearWatchdog();
          idx += 1;
          setTimeout(playNext, 60);
        };
        armWatchdog();
        try {
          window.speechSynthesis.speak(u);
        } catch (_e) {
          clearWatchdog();
          idx += 1;
          setTimeout(playNext, 60);
        }
      };
      playNext();
    }, 180);
  };

  const startRecording = async () => {
    try {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) return;
      const recognition = new SpeechRecognition();
      recognition.lang = 'en-US';
      recognition.interimResults = false;
      recognition.maxAlternatives = 1;
      recognition.continuous = false;
      mediaRecorderRef.current = recognition;
      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        if (transcript && transcript.trim()) sendMessage(transcript);
      };
      recognition.onerror = () => setIsRecording(false);
      recognition.onend = () => setIsRecording(false);
      recognition.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Speech recognition error:', error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col relative">
      <video autoPlay loop muted playsInline className="fixed inset-0 w-full h-full object-cover z-0">
        <source src={character.video} type="video/mp4" />
      </video>
      <div className="fixed inset-0 bg-white/10 backdrop-blur-[1px] z-10"></div>

      {/* Header */}
      <header className="relative z-20 bg-white/80 backdrop-blur-md border-b border-[#E0E0E0] px-3 sm:px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-shrink">
            <button onClick={handleBack} className="p-1.5 sm:p-2 rounded-full hover:bg-[#F3F0E6] transition-colors flex-shrink-0" data-testid="back-button">
              <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6 text-[#4A4A4A]" />
            </button>
            <a href="https://www.poodlesandfriends.uk/" target="_blank" rel="noopener noreferrer" className="hidden sm:block group relative" title="Visit our website">
              <img src={LOGO_IMAGE} alt="Poodles and Friends" className="h-20 w-auto object-contain transition-transform duration-300 group-hover:scale-105" />
              <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap bg-white/95 text-[#4A4A4A] px-3 py-1.5 rounded-full text-xs font-medium shadow-lg border border-[#E0E0E0] z-50">
                Visit our website? 🐩
              </div>
            </a>
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <img src={character.image} alt={character.name} className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover border-2 border-white shadow-md animate-gentle-bob flex-shrink-0" />
              <div className="min-w-0">
                <h2 className="text-base sm:text-xl font-bold text-[#4A4A4A] truncate">{character.name}</h2>
                {/* Subtitle hidden on small phones — name + footer credits is enough */}
                <p className="hidden sm:block text-[11px] sm:text-sm text-[#6B6B6B] truncate">Your poodle friend 🐩</p>
                {/* Credits line — hidden on mobile (it's already shown in the chat footer) */}
                <p className={`hidden sm:block text-sm font-semibold ${hotelSnapshot ? (orgTotalCredits <= 50 ? 'text-[#A55B64] animate-pulse' : 'text-[#6B6B6B]') : (paidRemaining > 0 && paidRemaining <= 10 ? 'text-[#A55B64] animate-pulse' : 'text-[#6B6B6B]')}`} data-testid="credits-display">
                  {hotelSnapshot
                    ? `${orgCounterLabel}: ${orgTotalCredits.toLocaleString()} remaining${orgTotalCredits <= 50 ? ' ⚠️' : ''}`
                    : (paidRemaining > 0
                        ? (freeRemaining > 0
                            ? `Free: ${freeRemaining} left · Credits: ${paidRemaining} saved`
                            : `Chat credits: ${paidRemaining} remaining${paidRemaining <= 10 ? ' ⚠️' : ''}`)
                        : `Free messages: ${freeRemaining} remaining`)}
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
            <div className="relative">
              <button onClick={() => setShowMusicMenu(!showMusicMenu)} data-testid="music-toggle-button"
                className={`p-2 sm:p-3 rounded-full transition-all duration-300 hover:scale-105 animate-white-glow ${isMusicPlaying ? 'bg-[#FFF7D6] text-[#B89823]' : 'bg-white/90 text-[#6B6B6B]'}`}>
                <Music className={`w-4 h-4 sm:w-6 sm:h-6 ${isMusicPlaying ? 'animate-music-bounce' : ''}`} strokeWidth={2.5} />
              </button>
            </div>
            <div className="relative">
              <button onClick={(e) => { e.stopPropagation(); setShowGamesMenu(!showGamesMenu); }} data-testid="games-button"
                className="p-2 sm:p-3 rounded-full bg-[#E8F5E9] text-[#5A8A5A] hover:bg-[#D0E8D0] transition-all duration-300 hover:scale-105 group relative">
                <Gamepad2 className="w-4 h-4 sm:w-6 sm:h-6" strokeWidth={2.5} />
                <div className="pointer-events-none absolute right-full mr-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap bg-white text-[#4A4A4A] px-3 py-2 rounded-xl text-xs font-medium shadow-lg border border-[#E0E0E0] z-50">
                  Play Games!
                </div>
              </button>
            </div>
            <button
              type="button"
              onClick={() => setShowLanguageModal(true)}
              data-testid="language-toggle-button"
              className="p-2 sm:p-3 rounded-full bg-[#E6F0F9] text-[#4A7AA5] hover:bg-[#C9DFE9] transition-all duration-300 hover:scale-105 group relative"
            >
              <Globe className="w-4 h-4 sm:w-6 sm:h-6" strokeWidth={2.5} />
              {secondaryLanguages.length > 0 && (
                <span
                  data-testid="language-badge"
                  className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-[#A55B64] text-white text-[9px] sm:text-[10px] font-bold flex items-center justify-center shadow"
                >
                  {secondaryLanguages.length}
                </span>
              )}
              <div className="pointer-events-none absolute right-full mr-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap bg-white text-[#4A4A4A] px-3 py-2 rounded-xl text-xs font-medium shadow-lg border border-[#E0E0E0] z-50">
                My Languages
                <div className="text-[#9A9A9A] text-[10px]">Pick up to 2 second languages</div>
              </div>
            </button>
            <a href="https://www.youtube.com/@PoodlesandFriends/videos" target="_blank" rel="noopener noreferrer" data-testid="youtube-button"
              className="p-2 sm:p-3 rounded-full bg-[#FFE4E4] text-[#FF0000] hover:bg-[#FFCCCC] transition-all duration-300 hover:scale-105 group relative">
              <Youtube className="w-4 h-4 sm:w-6 sm:h-6" strokeWidth={2.5} />
              <div className="pointer-events-none absolute right-full mr-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap bg-white text-[#4A4A4A] px-3 py-2 rounded-xl text-xs font-medium shadow-lg border border-[#E0E0E0] z-50">
                Watch Learning Videos
                <div className="text-[#9A9A9A] text-[10px]">Opens YouTube in new tab</div>
              </div>
            </a>
            {/* Subtle sign-in pill (right edge of header) — credits travel between WiFi/4G when signed in */}
            <PoodleTalkSignInPill compact />
          </div>
        </div>
      </header>

      {/* Chat Area */}
      <main className="flex-1 max-w-2xl w-full mx-auto p-4 overflow-hidden flex flex-col relative z-20">
        <ScrollArea className="flex-1 pr-4">
          <div className="space-y-4 sm:space-y-6 pb-4">
            {messages.map((msg, index) => (
              <ChatMessage 
                key={index} 
                message={msg.content} 
                isUser={msg.role === 'user'}
                poodleImage={character.image}
                userAvatar={userAvatar}
                userAvatarShape={userAvatarShape}
                learningTower={learningSubject ? TOWERS.find((t) => t.key === learningSubject) : null}
                onSpeak={msg.role === 'assistant' ? (text) => speakMessage(text, index) : null}
                isSpeaking={isSpeaking === index}
                bigText={bigText}
                isTyping={index === typingIndex}
                onTypingComplete={() => setTypingIndex(-1)}
              />
            ))}
            {isLoading && (
              <div className="flex items-end gap-3">
                <div className="relative flex-shrink-0">
                  <img src={character.image} alt={`${character.name} thinking`} className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover border-3 border-white shadow-md" />
                  {learningSubject && (() => {
                    const t = TOWERS.find((x) => x.key === learningSubject);
                    if (!t) return null;
                    const Tower = t.Comp;
                    return (
                      <div
                        className="absolute -top-5 -right-2 w-7 h-10 sm:w-8 sm:h-12 pointer-events-none lt-bob-2 drop-shadow-[0_2px_3px_rgba(0,0,0,0.25)]"
                        title={`${t.label} brain on!`}
                      >
                        <Tower />
                      </div>
                    );
                  })()}
                </div>
                <div className="bg-white border-2 border-[#EAE3D2] rounded-3xl rounded-tl-lg px-4 py-2 shadow-sm">
                  <TypingIndicator />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* Controls row removed — Voice + More functions now live under the typing box */}

        {/* Emotion Faces */}
        <div className="py-1 sm:py-2 px-2">
          <p className="text-white text-center mb-1.5 sm:mb-2 font-semibold text-base sm:text-2xl" style={{ textShadow: '0 1px 8px rgba(0,0,0,0.25)' }}>
            {ageMode === 'teen' ? "What's going on?" : "How are you feeling?"}
          </p>
          <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2">
            {ageMode === 'teen' ? (
              TEEN_EMOTIONS.map(e => (
                <EmotionFace key={e.emotion} emotion={e.emotion} face={e.face} label={e.label} 
                  onClick={() => handleEmotionClick(e.emotion)} bgColor={e.bgColor} borderColor={e.borderColor} />
              ))
            ) : (
              <>
                <EmotionFace emotion="happy" face="😊" label="Happy" onClick={() => handleEmotionClick('happy')} bgColor="bg-[#FFF7D6]" borderColor="border-[#FCEB9C]" />
                <EmotionFace emotion="sad" face="😢" label="Sad" onClick={() => handleEmotionClick('sad')} bgColor="bg-[#E6F0F9]" borderColor="border-[#C9DFE9]" />
                <EmotionFace emotion="nervous" face="😰" label="Nervous" onClick={() => handleEmotionClick('nervous')} bgColor="bg-[#FCEAEA]" borderColor="border-[#F3C4C4]" />
                <EmotionFace emotion="angry" face="😠" label="Angry" onClick={() => handleEmotionClick('angry')} bgColor="bg-[#FFE5E5]" borderColor="border-[#FFCCCC]" />
                <EmotionFace emotion="calm" face="😌" label="Calm" onClick={() => handleEmotionClick('calm')} bgColor="bg-[#E8F5E9]" borderColor="border-[#D0E8D0]" />
                <EmotionFace emotion="learning" face="🤓" label="Learning" onClick={() => handleEmotionClick('learning')} bgColor="bg-[#F3E8FF]" borderColor="border-[#E8D5FF]" />
              </>
            )}
          </div>
        </div>

        {/* Picture Cards + Avatar Maker + Learning Towers Buttons */}
        <div className="px-1 pb-1 flex justify-center gap-1 sm:gap-1.5 flex-nowrap">
          <button onClick={() => setShowPictureCards(true)} data-testid="picture-cards-button"
            className="flex items-center gap-1 px-2 sm:px-3 py-1 sm:py-1.5 bg-white border-2 border-[#E8E5DC] rounded-2xl text-[10px] sm:text-xs font-medium text-[#4A4A4A] hover:border-[#C9DFE9] hover:shadow-md transition-all animate-soft-glow whitespace-nowrap">
            <LayoutGrid className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> Picture Cards
          </button>
          <button onClick={() => setShowAvatarMaker(true)} data-testid="avatar-shortcut-button"
            className="flex items-center gap-1 px-2 sm:px-3 py-1 sm:py-1.5 bg-white border-2 border-[#E8E5DC] rounded-2xl text-[10px] sm:text-xs font-medium text-[#4A4A4A] hover:border-[#FFE57A] hover:shadow-md transition-all whitespace-nowrap">
            <span className="text-xs sm:text-sm leading-none">🎭</span> Avatar Maker
          </button>
          <button onClick={() => setShowLearningTowers(true)} data-testid="learning-towers-button"
            className="flex items-center gap-1 px-2 sm:px-3 py-1 sm:py-1.5 bg-white border-2 border-[#E8E5DC] rounded-2xl text-[10px] sm:text-xs font-medium text-[#4A4A4A] hover:border-[#FFE57A] hover:shadow-md transition-all whitespace-nowrap">
            <span className="text-xs sm:text-sm leading-none">🏰</span> Learning Towers
          </button>
        </div>

        {/* Subject pill + age chip — under the input area where the kid is looking */}
        <div className="px-2 pt-1 pb-1 flex justify-center gap-1.5 flex-wrap" data-testid="learning-bar">
          {learningSubject && (() => {
            const t = TOWERS.find((x) => x.key === learningSubject);
            if (!t) return null;
            return (
              <div className="flex items-center bg-white/95 backdrop-blur-sm rounded-full shadow-md border border-[#FFE89A] overflow-hidden text-[10px] sm:text-xs font-semibold">
                <button
                  onClick={() => setShowLearningTowers(true)}
                  data-testid="learning-subject-pill"
                  title="Switch tower"
                  className="flex items-center gap-1 pl-2.5 pr-1.5 py-1 text-[#B89823] hover:bg-[#FFF7D6] transition-colors"
                >
                  <span>{t.emoji}</span>
                  <span>{t.label} mode</span>
                </button>
                <button
                  onClick={exitTower}
                  data-testid="learning-subject-exit"
                  title="Leave tower"
                  className="px-1.5 py-1 text-[#9A9A9A] hover:text-[#A55B64] hover:bg-[#FCEAEA] border-l border-[#F0EAD6] transition-colors"
                >
                  ✕
                </button>
              </div>
            );
          })()}
          <button
            onClick={() => setAgeMode(ageMode === "tiny" ? "kids" : ageMode === "kids" ? "teen" : "tiny")}
            data-testid="age-chip"
            title="Tap to change age mode"
            className="flex items-center gap-1 bg-white/95 backdrop-blur-sm rounded-full shadow-sm border border-[#E0E0E0] px-2.5 py-1 text-[10px] sm:text-xs font-medium text-[#6B6B6B] hover:bg-white hover:border-[#FFE89A] transition-colors"
          >
            <span>{ageMode === "tiny" ? "🐣" : ageMode === "teen" ? "🌟" : "🌱"}</span>
            <span>{ageMode === "tiny" ? "Tiny (3–5)" : ageMode === "teen" ? "Older (11–17)" : "Younger (5–10)"}</span>
            <span className="text-[#B89823]">✏️</span>
          </button>
        </div>

        {/* Quick Messages */}
        <div className="px-2 pb-1">
          <button onClick={() => setShowAAC(!showAAC)} data-testid="aac-toggle"
            className={`w-full text-center py-1 transition-colors underline ${showAAC ? 'text-white' : 'text-white/70 hover:text-white'}`}
            style={{ fontSize: '13px' }}>
            {(() => {
              if (ageMode === 'tiny') return showAAC ? 'Hide picture buttons' : 'Tap a picture! 🐩';
              if (ageMode === 'teen') return showAAC ? 'Hide quick messages' : 'Quick things to say';
              return showAAC ? 'Hide quick cards' : 'More ways to talk';
            })()}
          </button>
          {showAAC && (
            <div className="flex flex-wrap justify-center gap-2 mt-2 animate-fade-in-up">
              {(ageMode === 'tiny' ? TINY_AAC_CARDS : ageMode === 'teen' ? TEEN_QUICK_MSGS : AAC_CARDS).map(card => (
                <EmotionFace key={card.id} emotion={card.id} face={card.face} label={card.label} 
                  onClick={() => { sendCardMessage(`${card.face} ${card.text}`, () => getQuickMsgResponse(card.id)); trackTap("quick_message"); }} bgColor={card.bgColor} borderColor={card.borderColor} />
              ))}
            </div>
          )}
        </div>

        {/* Input */}
        <form onSubmit={handleSubmit} className="flex gap-2 pb-4">
          <button type="button" onClick={isRecording ? stopRecording : startRecording} disabled={isLoading} data-testid="mic-button"
            className={`p-4 sm:p-5 rounded-full transition-all duration-300 hover:scale-105 disabled:opacity-50 ${isRecording ? 'bg-[#FF6B6B] text-white animate-pulse' : 'bg-[#F3F0E6] text-[#6B6B6B] hover:bg-[#E8E5DC]'}`}
            title={isRecording ? 'Stop recording' : 'Talk to poodle'}>
            {isRecording ? <MicOff className="w-6 h-6 sm:w-7 sm:h-7" strokeWidth={2.5} /> : <Mic className="w-6 h-6 sm:w-7 sm:h-7" strokeWidth={2.5} />}
          </button>
          <div className="flex-1 relative">
            <input type="text" value={inputValue} onChange={(e) => setInputValue(e.target.value)}
              placeholder={isRecording ? "Listening..." : "Type or tap mic to talk..."} data-testid="chat-input-field"
              className="w-full px-5 py-4 sm:py-5 text-lg sm:text-xl bg-white/90 backdrop-blur-sm border-2 border-[#EAE3D2] rounded-full focus:outline-none focus:border-[#C9B896] focus:ring-2 focus:ring-[#C9B896]/20 transition-all placeholder:text-[#9A9A9A]"
              disabled={isLoading || isRecording} />
            <Sparkles className="absolute right-5 top-1/2 -translate-y-1/2 w-5 h-5 text-[#C9B896] opacity-50" />
          </div>
          <Button type="submit" disabled={isLoading || !inputValue.trim() || isRecording} data-testid="chat-submit-button"
            className="p-4 sm:p-5 rounded-full bg-[#E8D4A8] hover:bg-[#D9C599] text-[#5A4A32] transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:hover:scale-100">
            <Send className="w-6 h-6 sm:w-7 sm:h-7" strokeWidth={2.5} />
          </Button>
        </form>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-1.5 sm:gap-3 pb-1">
          <p className={`text-xs sm:text-sm font-semibold text-center ${hotelSnapshot ? (orgTotalCredits <= 50 ? 'text-[#A55B64] animate-pulse' : 'text-[#E8D4A8]') : (paidRemaining > 0 && paidRemaining <= 10 ? 'text-[#A55B64] animate-pulse' : 'text-[#E8D4A8]')}`} data-testid="credits-display-bottom"
            style={{ textShadow: '0 0 10px rgba(232,212,168,0.6), 0 0 20px rgba(232,212,168,0.3)' }}>
            {hotelSnapshot
              ? `${orgCounterLabel}: ${orgTotalCredits.toLocaleString()} remaining${orgTotalCredits <= 50 ? ' ⚠️' : ''}`
              : (paidRemaining > 0
                  ? (freeRemaining > 0
                      ? `Free: ${freeRemaining} left · Credits: ${paidRemaining} saved`
                      : `Chat credits: ${paidRemaining} remaining${paidRemaining <= 10 ? ' ⚠️' : ''}`)
                  : `Free messages: ${freeRemaining} remaining`)}
          </p>
          <div className="flex items-center justify-center gap-2">
            <div className="relative">
              <button
                onClick={(e) => { e.stopPropagation(); setShowMoreFunctions(v => !v); }}
                data-testid="more-functions-button"
                aria-label="More options"
                className="flex items-center justify-center gap-1 sm:px-3 sm:py-1 sm:bg-white/90 sm:text-[#4A4A4A] sm:rounded-full sm:text-xs sm:font-medium sm:hover:bg-white transition-all sm:shadow-sm whitespace-nowrap leading-tight sm:h-6 text-white/90 text-2xl tracking-[0.2em] drop-shadow hover:text-white px-2"
              >
                <span className="sm:hidden">•••</span>
                <span className="hidden sm:inline">⚙️ More functions</span>
              </button>
            {showMoreFunctions && (
              <div
                className="absolute right-0 sm:left-1/2 sm:-translate-x-1/2 bottom-full mb-2 bg-white rounded-2xl shadow-lg border border-[#E0E0E0] p-2 z-50 min-w-[220px]"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => { setShowParentsCorner(true); setShowMoreFunctions(false); }}
                  data-testid="parents-corner-button"
                  className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-xs hover:bg-[#FFF7D6] hover:text-[#B89823] text-[#4A4A4A] font-medium transition-all border-b border-[#F0EAD6] mb-2 pb-2"
                >
                  <Info className="w-3.5 h-3.5" /> Parents / Grown Up
                </button>
                <p className="text-[10px] text-[#9A9A9A] px-3 pt-1 pb-1.5 uppercase tracking-wider">Poodle Voice</p>
                <div className="px-1 pb-2 grid grid-cols-2 gap-1 border-b border-[#F0EAD6] mb-2">
                  {VOICE_OPTIONS.map(voice => (
                    <button
                      key={voice.id}
                      onClick={() => { setSelectedVoice(voice.id); }}
                      data-testid={`more-fn-voice-${voice.id}`}
                      className={`text-left px-2 py-1.5 rounded-lg text-xs transition-all ${selectedVoice === voice.id ? 'bg-[#FFF7D6] text-[#B89823] font-semibold' : 'hover:bg-[#F3F0E6] text-[#4A4A4A]'}`}
                    >
                      {voice.name}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => { setAutoRead(!autoRead); }}
                  data-testid="more-fn-auto-read"
                  className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-xs transition-all ${autoRead ? 'bg-[#E8F5E9] text-[#5A8A5A]' : 'hover:bg-[#F3F0E6] text-[#4A4A4A]'}`}
                >
                  <Volume2 className="w-3.5 h-3.5" /> {autoRead ? 'Auto-Read ON' : 'Auto-Read'}
                </button>
                <button
                  onClick={() => { setBigText(!bigText); }}
                  data-testid="more-fn-bigger"
                  className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-xs transition-all ${bigText ? 'bg-[#E6F0F9] text-[#4A7AA5]' : 'hover:bg-[#F3F0E6] text-[#4A4A4A]'}`}
                >
                  {bigText ? <ZoomOut className="w-3.5 h-3.5" /> : <ZoomIn className="w-3.5 h-3.5" />}
                  {bigText ? 'Smaller text' : 'Bigger text'}
                </button>
                <button
                  onClick={() => { setShowBreathing(true); setShowMoreFunctions(false); }}
                  data-testid="more-fn-breathe"
                  className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-xs hover:bg-[#E8F5E9] hover:text-[#5A8A5A] text-[#4A4A4A] transition-all"
                >
                  <Wind className="w-3.5 h-3.5" /> Breathe
                </button>
                <button
                  onClick={() => { setShowCalmTimer(true); setShowMoreFunctions(false); }}
                  data-testid="more-fn-calm"
                  className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-xs hover:bg-[#E6F0F9] hover:text-[#4A7AA5] text-[#4A4A4A] transition-all"
                >
                  <Timer className="w-3.5 h-3.5" /> Calm Down
                </button>
              </div>
            )}
          </div>
          </div>
        </div>

        <p className="text-center text-sm text-white pb-2">
          {ageMode === 'teen' ? "If something feels wrong or you're struggling, please reach out to someone you trust 💛" : "If you need help or feel worrying, please talk to a grown-up you trust 💛"}
        </p>
      </main>

      {/* Music Menu */}
      {showMusicMenu && (
        <>
          <div className="fixed inset-0 z-[55]" onClick={() => setShowMusicMenu(false)}></div>
          <div className="fixed top-1/3 left-1/2 -translate-x-1/2 bg-white rounded-2xl shadow-2xl border border-[#E0E0E0] p-4 z-[60] w-[75vw] max-w-[240px]">
            <div className="flex justify-between items-center mb-3">
              <p className="text-sm font-semibold text-[#4A4A4A]">Choose a song</p>
              <button onClick={() => setShowMusicMenu(false)} className="p-1 rounded-full hover:bg-[#F3F0E6] text-[#9A9A9A] hover:text-[#4A4A4A]">
                <X className="w-5 h-5" />
              </button>
            </div>
            {BACKGROUND_MUSIC.map((song, index) => (
              <button key={index} onClick={() => selectSong(index)}
                className={`w-full text-left px-3 py-2 rounded-xl text-sm transition-all mb-1 ${currentSong === index ? 'bg-[#FFF7D6] text-[#B89823] font-semibold' : 'hover:bg-[#F3F0E6] text-[#4A4A4A]'}`}>
                {song.name}{currentSong === index && isMusicPlaying && ' ♪'}
              </button>
            ))}
            <div className="border-t border-[#E0E0E0] mt-2 pt-2">
              <button onClick={toggleMusic}
                className={`w-full px-3 py-2 rounded-xl text-sm font-semibold transition-all ${isMusicPlaying ? 'bg-[#FCEAEA] text-[#A55B64] hover:bg-[#F3C4C4]' : 'bg-[#E8F5E9] text-[#5A8A5A] hover:bg-[#D0E8D0]'}`}>
                {isMusicPlaying ? 'Pause Music' : 'Play Music'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Footer */}
      <footer className="relative z-20 py-2 px-4">
        <div className="text-center text-xs text-white">
          <a href="https://www.poodlesandfriends.uk/" target="_blank" rel="noopener noreferrer" className="hover:text-[#6B6B6B] transition-colors">
            © 2026 Poodle Talk. All rights reserved. | Poodles & Friends is a family-friendly educational music and media project. | Made for kids
          </a>
          <span className="mx-2">·</span>
          <button onClick={() => setShowParentsCorner(true)} className="hover:text-[#6B6B6B] transition-colors">Parents Corner</button>
          <span className="mx-2">·</span>
          <a href="https://www.poodlesandfriends.uk/policy-info" target="_blank" rel="noopener noreferrer" className="hover:text-[#6B6B6B] transition-colors">Full Policy</a>
        </div>
        <div className="text-center text-[10px] text-white/70 mt-1 px-2 max-w-2xl mx-auto">
          Registered with the Information Commissioner&apos;s Office (ICO). We process personal data in accordance with UK data protection legislation.
        </div>
      </footer>

      {/* Modals */}
      <BreathingExercise isOpen={showBreathing} onClose={() => setShowBreathing(false)} />
      <PuzzleGame isOpen={showPuzzle} onClose={() => setShowPuzzle(false)} />
      <BubblePopGame isOpen={showBubblePop} onClose={() => setShowBubblePop(false)} />
      <MemoryMatchGame isOpen={showMemoryMatch} onClose={() => setShowMemoryMatch(false)} />
      <PoodleMaths isOpen={showMaths} onClose={() => setShowMaths(false)} />
      <ColouringBook isOpen={showColouring} onClose={() => setShowColouring(false)} />
      <SpotDifference isOpen={showSpotDiff} onClose={() => setShowSpotDiff(false)} />

      {/* Games Menu Overlay */}
      {showGamesMenu && (
        <div className="fixed inset-0 z-[80] bg-black/30 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowGamesMenu(false)} data-testid="games-menu-backdrop">
          <div className="bg-white rounded-3xl shadow-2xl border border-[#E8E5DC] overflow-hidden w-64 text-center relative" 
            onClick={(e) => e.stopPropagation()} data-testid="games-menu">
            <button onClick={() => setShowGamesMenu(false)} className="absolute top-3 right-3 text-[#9A9A9A] hover:text-[#4A4A4A] transition-colors">
              <X className="w-5 h-5" />
            </button>
            <p className="px-4 pt-5 pb-2 text-sm font-bold text-[#4A4A4A]">Choose a Game</p>
            <div className="px-3 pb-4 space-y-2">
              <button onClick={() => { setShowPuzzle(true); setShowGamesMenu(false); }} data-testid="games-menu-puzzle"
                className="w-full px-4 py-3.5 text-sm text-[#4A4A4A] bg-[#F9F6EE] hover:bg-[#F3F0E6] rounded-2xl transition-colors flex items-center gap-3 font-medium">
                <span className="text-2xl">🧩</span> Poodle Puzzles
              </button>
              <button onClick={() => { setShowBubblePop(true); setShowGamesMenu(false); }} data-testid="games-menu-bubble"
                className="w-full px-4 py-3.5 text-sm text-[#4A4A4A] bg-[#F9F6EE] hover:bg-[#F3F0E6] rounded-2xl transition-colors flex items-center gap-3 font-medium">
                <span className="text-2xl">🫧</span> Bubble Pop
              </button>
              <button onClick={() => { setShowMemoryMatch(true); setShowGamesMenu(false); }} data-testid="games-menu-memory"
                className="w-full px-4 py-3.5 text-sm text-[#4A4A4A] bg-[#F9F6EE] hover:bg-[#F3F0E6] rounded-2xl transition-colors flex items-center gap-3 font-medium">
                <span className="text-2xl">🐩</span> Poodle Match
              </button>
              <button onClick={() => { setShowMaths(true); setShowGamesMenu(false); }} data-testid="games-menu-maths"
                className="w-full px-4 py-3.5 text-sm text-[#4A4A4A] bg-[#F9F6EE] hover:bg-[#F3F0E6] rounded-2xl transition-colors flex items-center gap-3 font-medium">
                <span className="text-2xl">🧮</span> Poodle Maths
              </button>
              <button onClick={() => { setShowColouring(true); setShowGamesMenu(false); }} data-testid="games-menu-colouring"
                className="w-full px-4 py-3.5 text-sm text-[#4A4A4A] bg-[#F9F6EE] hover:bg-[#F3F0E6] rounded-2xl transition-colors flex items-center gap-3 font-medium">
                <span className="text-2xl">🎨</span> Colouring Book
              </button>
              <button onClick={() => { setShowSpotDiff(true); setShowGamesMenu(false); }} data-testid="games-menu-spotdiff"
                className="w-full px-4 py-3.5 text-sm text-[#4A4A4A] bg-[#F9F6EE] hover:bg-[#F3F0E6] rounded-2xl transition-colors flex items-center gap-3 font-medium">
                <span className="text-2xl">🔍</span> Spot the Differences
              </button>
              <button onClick={() => { setShowAvatarMaker(true); setShowGamesMenu(false); }} data-testid="games-menu-avatar"
                className="w-full px-4 py-3.5 text-sm text-[#4A4A4A] bg-[#F9F6EE] hover:bg-[#F3F0E6] rounded-2xl transition-colors flex items-center gap-3 font-medium">
                <span className="text-2xl">🎭</span> Avatar Maker
              </button>
              <a
                href="https://www.poodlesandfriends.uk/learning-resources/learning-song-sheets"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setShowGamesMenu(false)}
                data-testid="games-menu-song-sheets"
                className="w-full px-4 py-3.5 text-sm text-[#4A4A4A] bg-[#F9F6EE] hover:bg-[#F3F0E6] rounded-2xl transition-colors flex items-center gap-3 font-medium"
              >
                <span className="text-2xl">🎵</span> Song Sheets
              </a>
              <a
                href="https://www.poodlesandfriends.uk/learning-resources"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setShowGamesMenu(false)}
                data-testid="games-menu-more-resources"
                className="w-full px-4 py-3.5 text-sm text-[#4A4A4A] bg-[#F9F6EE] hover:bg-[#F3F0E6] rounded-2xl transition-colors flex items-center gap-3 font-medium"
              >
                <span className="text-2xl">📚</span> More Free Resources
              </a>
            </div>
          </div>
        </div>
      )}
      <ParentsCorner isOpen={showParentsCorner} onClose={() => setShowParentsCorner(false)} onCreditsUpdate={(paid) => { setPaidRemaining(paid); setIsOnCooldown(false); }} onOpenUsageReport={() => onNavigate && onNavigate('hotel-usage-report')} />
      <CalmDownTimer isOpen={showCalmTimer} onClose={() => setShowCalmTimer(false)} />
      <PictureCardsModal isOpen={showPictureCards} onClose={() => setShowPictureCards(false)} onSendCardMessage={(text, cardId) => { sendCardMessage(text, () => getCardResponse(cardId)); trackTap("card"); }} onCategoryOpen={(cat) => trackTap("category_open", cat)} />
      <AvatarMaker isOpen={showAvatarMaker} onClose={() => setShowAvatarMaker(false)} />
      <LanguageToggleModal
        isOpen={showLanguageModal}
        onClose={() => setShowLanguageModal(false)}
        onSaved={(langs) => setSecondaryLanguages(langs)}
        paidRemaining={paidRemaining}
      />

      {/* Learning Towers overlay — kid taps a tower → Poodle becomes a tutor for that subject */}
      {showLearningTowers && (
        <LearningTowers
          ageMode={ageMode}
          onPick={enterTower}
          onClose={() => setShowLearningTowers(false)}
          requiresCredits={paidRemaining <= 0}
        />
      )}

      {/* Sleepy-poodle banner — non-blocking. Replaces the old full-screen
          cooldown overlay (which had z-index issues breaking Games & Picture
          Cards). Picture Cards, Avatar Maker, emotion buttons and games all
          remain fully usable because nothing is overlaid on the chat. */}
      {isOnCooldown && (
        <div className="fixed top-2 left-1/2 -translate-x-1/2 z-30 max-w-[92%] sm:max-w-md w-full px-3" data-testid="sleepy-banner">
          <div className="bg-[#FFF7D6] border-2 border-[#FCEB9C] rounded-2xl px-3 py-2 shadow-lg flex items-center gap-2">
            <span className="text-xl shrink-0">💤</span>
            <p className="text-[11px] sm:text-xs text-[#5A4A32] leading-tight flex-1">
              {globalCapHit
                ? <>Poodle Talk is very busy 💛 Picture Cards &amp; Games still work.</>
                : <>Your poodle is having a snooze. Picture Cards &amp; Games still work — or unlock more chats below.</>
              }
            </p>
            <button onClick={() => { if(onNavigate) onNavigate('buy'); }} data-testid="sleepy-buy-credits"
              className="shrink-0 text-[10px] sm:text-xs px-2.5 py-1 bg-[#E8D4A8] text-[#5A4A32] rounded-full font-semibold hover:bg-[#D9C599] transition-colors whitespace-nowrap">
              ✨ Top up
            </button>
          </div>
        </div>
      )}


      {/* Low Credit Warning Popup */}
      {showLowCreditWarning && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="low-credit-warning">
          <div className="bg-[#FDFBF7] rounded-3xl p-6 sm:p-8 max-w-sm w-full text-center shadow-2xl relative">
            <button onClick={() => setShowLowCreditWarning(false)} className="absolute top-4 right-4 text-[#6B6B6B] hover:text-[#4A4A4A]">
              <X className="w-6 h-6" />
            </button>
            <div className="text-5xl mb-4">💛</div>
            <h2 className="text-xl font-bold text-[#4A4A4A] mb-2">Your credits are getting low</h2>
            <p className="text-sm text-[#6B6B6B] mb-5">
              You have <strong className="text-[#A55B64]">{paidRemaining} credits</strong> remaining.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => { setShowLowCreditWarning(false); if(onNavigate) onNavigate('buy'); }}
                data-testid="low-credit-topup-link"
                className="block w-full px-5 py-3 bg-[#E8D4A8] text-[#5A4A32] rounded-full text-sm font-semibold hover:bg-[#D9C599] transition-all"
              >
                Top up credits
              </button>
              <button
                onClick={() => setShowLowCreditWarning(false)}
                data-testid="low-credit-dismiss"
                className="block w-full px-5 py-3 bg-[#F3F0E6] text-[#6B6B6B] rounded-full text-sm font-medium hover:bg-[#E8E5DC] transition-all"
              >
                Keep chatting ({paidRemaining} left)
              </button>
            </div>
            <p className="text-xs text-[#9A9A9A] mt-4">
              Picture Cards, Avatar Maker and Games are always free to play 💛
            </p>
          </div>
        </div>
      )}


      {/* Mood Check */}
      {showMoodCheck && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="mood-check-modal">
          <div className="bg-[#FDFBF7] rounded-3xl p-8 max-w-sm w-full text-center shadow-2xl relative">
            <button onClick={() => setShowMoodCheck(false)} className="absolute top-4 right-4 text-[#6B6B6B] hover:text-[#4A4A4A]">
              <X className="w-7 h-7" />
            </button>
            <h2 className="text-xl font-bold text-[#4A4A4A] mb-2">How do you feel now?</h2>
            <p className="text-sm text-[#9A9A9A] mb-5">After chatting with {character.name}</p>
            <div className="flex justify-center gap-4 mb-4">
              {[
                { face: '😊', label: 'Better', color: 'bg-[#E8F5E9] border-[#D0E8D0]' },
                { face: '😐', label: 'Same', color: 'bg-[#FFF7D6] border-[#FCEB9C]' },
                { face: '😢', label: 'Still sad', color: 'bg-[#E6F0F9] border-[#C9DFE9]' },
              ].map(mood => (
                <button key={mood.label} onClick={() => {
                    const moods = JSON.parse(localStorage.getItem('poodle-moods') || '[]');
                    moods.push({ mood: mood.label, character: character.name, time: new Date().toISOString() });
                    localStorage.setItem('poodle-moods', JSON.stringify(moods));
                    setShowMoodCheck(false);
                    onBack();
                  }}
                  data-testid={`mood-${mood.label.toLowerCase().replace(' ', '-')}`}
                  className={`flex flex-col items-center gap-2 p-4 rounded-2xl border-2 ${mood.color} hover:scale-110 transition-all`}>
                  <span className="text-3xl">{mood.face}</span>
                  <span className="text-xs font-medium text-[#4A4A4A]">{mood.label}</span>
                </button>
              ))}
            </div>
            <p className="text-xs text-[#9A9A9A]">Your feelings are always valid 💛</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatScreen;
